export type DashboardLoadState = 'initializing' | 'unauthenticated' | 'loading' | 'ready'

export interface DashboardLoadStateInput {
  /** Whether the account/auth context has finished its initial bootstrap. */
  initialized: boolean
  /** Whether an authenticated user is present. */
  hasUser: boolean
  /** react-query `isLoading` for the single-dashboard query (`isPending && isFetching`). */
  dashboardIsLoading: boolean
}

/**
 * Decide what a dashboard page should render without ever hanging on a
 * permanently-disabled query.
 *
 * React Query v5 reports `isPending === true` for a *disabled* query forever,
 * and the single-dashboard query is disabled while `account.user` is falsy.
 * Gating a full-page spinner on `isPending` therefore spins indefinitely on
 * direct-URL access when the session is missing/expired — which is why the
 * internal dashboard view showed the "wheel of death" while the public shared
 * link (which checks auth first) still worked.
 *
 * Resolution order:
 *  - auth still bootstrapping       -> 'initializing'   (brief spinner)
 *  - bootstrapped but no user       -> 'unauthenticated' (redirect to login)
 *  - authenticated, query in flight -> 'loading'         (spinner)
 *  - otherwise                      -> 'ready'
 *
 * Note we deliberately rely on `dashboardIsLoading` (not `isPending`): once a
 * user is present the query is enabled and reports `isLoading` while fetching,
 * so there is no flash of undefined data — and a query that stays disabled
 * (e.g. the editor's `dashboardId === 'new'` case) resolves to 'ready'.
 */
export function resolveDashboardLoadState({
  initialized,
  hasUser,
  dashboardIsLoading,
}: DashboardLoadStateInput): DashboardLoadState {
  if (!initialized) return 'initializing'
  if (!hasUser) return 'unauthenticated'
  if (dashboardIsLoading) return 'loading'
  return 'ready'
}
